import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { createEngin, updateEngin } from '@/store/thunks/enginThunks';
import type { Engin } from '@/types';
import { fetchFamilles } from '@/store/thunks/familleThunks';
import { fetchTiers } from '@/store/thunks/tierThunks';
import { Input, Button, Select, ToggleSwitch } from '@/components/ui';

function toDateInputValue(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v.length >= 10 ? v.slice(0, 10) : v;
  try {
    const d = new Date(v as string);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

const schema = Yup.object({
  code: Yup.string().required('Code obligatoire'),
  familleId: Yup.string().required('Famille obligatoire'),
  type: Yup.string().required('Type obligatoire'),
});

interface EnginFormProps {
  engin?: Engin | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EnginForm({ engin, type, onClose, onSuccess }: EnginFormProps) {
  const dispatch = useAppDispatch();
  const familles = useAppSelector((s: RootState) => s.famille.familles);
  const tiers = useAppSelector((s: RootState) => s.tier.tiers);

  useEffect(() => {
    dispatch(fetchFamilles());
    dispatch(fetchTiers());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      code: engin?.code ?? '',
      type: engin?.type ?? '',
      unite: engin?.unite ?? '',
      familleId: (engin as unknown as { famille?: { id?: number } })?.famille?.id != null ? String((engin as unknown as { famille?: { id?: number } }).famille?.id) : '',
      tierId: (engin as unknown as { tier?: { id?: number } })?.tier?.id != null ? String((engin as unknown as { tier?: { id?: number } }).tier?.id) : '',
      prisEnCharge: String(Boolean((engin as unknown as { prisEnCharge?: boolean })?.prisEnCharge ?? false)),
      reservoir: String((engin as unknown as { reservoir?: number })?.reservoir ?? ''),
      prixUnitaire: String((engin as unknown as { prixUnitaire?: number })?.prixUnitaire ?? ''),
      current_cpt: String((engin as unknown as { current_cpt?: number })?.current_cpt ?? ''),
      consommation_min: String((engin as unknown as { consommation_min?: number })?.consommation_min ?? ''),
      consommation_max: String((engin as unknown as { consommation_max?: number })?.consommation_max ?? ''),
      current_consommation: String((engin as unknown as { current_consommation?: number })?.current_consommation ?? ''),
      date_last_consommation: toDateInputValue((engin as unknown as { date_last_consommation?: string })?.date_last_consommation),
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        code: values.code,
        unite: values.unite || undefined,
        type: values.type || undefined,
        prisEnCharge: values.prisEnCharge === 'true',
        reservoir: values.reservoir === '' ? 0 : Number(values.reservoir),
        prixUnitaire: values.prixUnitaire === '' ? 0 : Number(values.prixUnitaire),
        current_cpt: values.current_cpt === '' ? 0 : Number(values.current_cpt),
        consommation_min: values.consommation_min === '' ? 0 : Number(values.consommation_min),
        consommation_max: values.consommation_max === '' ? 0 : Number(values.consommation_max),
        current_consommation: values.current_consommation === '' ? 0 : Number(values.current_consommation),
        date_last_consommation: values.date_last_consommation ? new Date(values.date_last_consommation).toISOString() : undefined,
        famille: values.familleId ? { id: Number(values.familleId), nom: undefined } : undefined,
        tier: values.tierId ? { id: Number(values.tierId), nom: undefined } : undefined,
      };
      if (type === 'update' && engin?.id) {
        const res = await dispatch(updateEngin({ ...engin, ...payload } as Engin));
        if (updateEngin.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createEngin(payload));
        if (createEngin.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const typeOptions = [
    { value: 'ENGIN', label: 'ENGIN' },
    { value: 'TRANSPORT', label: 'TRANSPORT' },
    { value: 'VOITURE', label: 'VOITURE' },
    { value: 'CAMION', label: 'CAMION' },
  ];
  const familleOptions = familles.map((f) => ({ value: String(f.id), label: f.nom ?? String(f.id) }));
  const tierOptions = tiers.map((t) => ({ value: String(t.id), label: t.nom ?? t.code ?? String(t.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        <Input
          id="code"
          placeholder="Code"
          aria-label="Code"
          {...formik.getFieldProps('code')}
          error={formik.touched.code ? formik.errors.code : undefined}
        />

        <Select
          id="type"
          options={typeOptions}
          value={formik.values.type}
          onChange={(e) => formik.setFieldValue('type', e.target.value)}
          onBlur={formik.handleBlur}
          placeholder="Type"
          aria-label="Type"
          searchable
          error={formik.touched.type && formik.errors.type ? String(formik.errors.type) : undefined}
        />

        <Input
          id="unite"
          placeholder="Unite"
          aria-label="Unite"
          {...formik.getFieldProps('unite')}
        />

        <Input
          id="prixUnitaire"
          placeholder="Prix Unite"
          aria-label="Prix Unite"
          type="number"
          step="any"
          {...formik.getFieldProps('prixUnitaire')}
        />

        <Select
          id="familleId"
          options={familleOptions}
          value={formik.values.familleId}
          onChange={(e) => formik.setFieldValue('familleId', e.target.value)}
          onBlur={formik.handleBlur}
          placeholder="Famille"
          searchable
          aria-label="Famille"
          error={formik.touched.familleId && formik.errors.familleId ? String(formik.errors.familleId) : undefined}
        />

        <Input
          id="reservoir"
          placeholder="Reservoir"
          aria-label="Reservoir"
          type="number"
          step="any"
          {...formik.getFieldProps('reservoir')}
        />

        <Input
          id="current_cpt"
          placeholder="Compteur Actual"
          aria-label="Compteur Actual"
          type="number"
          step="any"
          {...formik.getFieldProps('current_cpt')}
        />

        <Select
          id="tierId"
          options={tierOptions}
          value={formik.values.tierId}
          onChange={(e) => formik.setFieldValue('tierId', e.target.value)}
          onBlur={formik.handleBlur}
          placeholder="Locatier"
          searchable
          aria-label="Locatier"
        />

        <Input
          id="consommation_min"
          placeholder="Consommation Min"
          aria-label="Consommation Min"
          type="number"
          step="any"
          {...formik.getFieldProps('consommation_min')}
        />

        <Input
          id="consommation_max"
          placeholder="Consommation Max"
          aria-label="Consommation Max"
          type="number"
          step="any"
          {...formik.getFieldProps('consommation_max')}
        />
      </div>

      <ToggleSwitch
        label="Externe"
        description="Activer si l’engin est pris en charge par un locatier."
        checked={formik.values.prisEnCharge === 'true'}
        onCheckedChange={(v) => formik.setFieldValue('prisEnCharge', String(v))}
      />

      <Button type="submit" loading={formik.isSubmitting} fullWidth size="lg">
        {type === 'update' ? 'MODIFIER' : 'CREER'}
      </Button> 
    </form>
  );
}
